import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'

export interface AgentStartBackdropProps {
  /**
   * When set, the image becomes the backdrop and the drifting blobs are not
   * rendered at all. This is the whole customer-imagery hook (#1294): a Sky
   * asset arrives, one prop changes, nothing else on the page moves.
   */
  imageUrl?: string
  /**
   * The cover's catalogue slug, exposed as `data-cover` alongside
   * `data-backdrop-kind` so tests — and the later "about this image" /
   * favourite affordances (#1294 follow-up) — can read which image is on
   * screen without re-deriving it.
   */
  slug?: string
  /**
   * Beat 1 of Send (#1294 follow-up): the ground goes plain. Every layer this
   * component draws — cover images, the wash over them, the blobs — fades to
   * nothing over {@link DIM_MS}, leaving the app's own neutral behind. The
   * column above keeps its own timing; this prop only owns the ground.
   */
  dimmed?: boolean
}

/** Beat 1 of the send transition: how long the ground takes to go plain. */
export const DIM_MS = 300

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

/**
 * The calm ground the agent start screen sits on.
 *
 * With no `imageUrl`, two blue blobs and one slate blob, all under 12%
 * opacity on the app's light neutral, drifting on a 60s loop. Only
 * `transform` is animated, so the whole thing stays on the compositor and
 * never repaints the column above it. Under `prefers-reduced-motion` the
 * blobs are placed and then hold still — the composition is the same, the
 * movement is not.
 *
 * With an `imageUrl`, a customer cover image (#1294 follow-up: Sky's own
 * cover images, cycled via a refresh button on the start screen). Changing
 * `imageUrl` crossfades: the previous image stays layered underneath while
 * the new one fades in over 500ms, then the old layer unmounts. Reduced
 * motion skips the fade — the new image simply replaces the old one.
 */
export default function AgentStartBackdrop({ imageUrl, slug, dimmed }: AgentStartBackdropProps) {
  // Two layers so a change in `imageUrl` can crossfade rather than cut: the
  // previous image is kept mounted underneath the incoming one until its
  // fade-in has had time to play, then it is dropped.
  const [layers, setLayers] = useState<{ current?: string; previous?: string }>({ current: imageUrl })
  const lastImageUrlRef = useRef(imageUrl)
  if (imageUrl !== lastImageUrlRef.current) {
    lastImageUrlRef.current = imageUrl
    setLayers(prev => ({ current: imageUrl, previous: prev.current }))
  }

  // Drop the previous layer once its fade-in has played. This is lifecycle
  // cleanup tied to one trigger (a new previous layer arriving), so a timer
  // is the right tool — not a derived-state effect.
  useEffect(() => {
    if (layers.previous === undefined) return
    const ms = prefersReducedMotion() ? 0 : 500
    const timer = setTimeout(() => {
      setLayers(l => (l.previous === undefined ? l : { current: l.current, previous: undefined }))
    }, ms)
    return () => clearTimeout(timer)
  }, [layers.previous])

  return (
    <Box
      data-testid="agent-start-backdrop"
      data-backdrop-kind={imageUrl ? 'image' : 'blobs'}
      data-cover={slug}
      data-cover-url={imageUrl || undefined}
      data-dimmed={dimmed ? 'true' : undefined}
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        backgroundColor: 'background.default',
        // Transform-only drift. Long, slow, and out of phase so the field never
        // reads as a loop.
        '@keyframes agentStartDriftA': {
          '0%':   { transform: 'translate3d(0, 0, 0) scale(1)' },
          '50%':  { transform: 'translate3d(6%, 4%, 0) scale(1.08)' },
          '100%': { transform: 'translate3d(0, 0, 0) scale(1)' },
        },
        '@keyframes agentStartDriftB': {
          '0%':   { transform: 'translate3d(0, 0, 0) scale(1.04)' },
          '50%':  { transform: 'translate3d(-5%, -6%, 0) scale(1)' },
          '100%': { transform: 'translate3d(0, 0, 0) scale(1.04)' },
        },
        '@keyframes agentStartDriftC': {
          '0%':   { transform: 'translate3d(0, 0, 0) scale(1)' },
          '50%':  { transform: 'translate3d(4%, -5%, 0) scale(1.06)' },
          '100%': { transform: 'translate3d(0, 0, 0) scale(1)' },
        },
        '@keyframes agentStartCoverFadeIn': {
          '0%':   { opacity: 0 },
          '100%': { opacity: 1 },
        },
      }}
    >
      {imageUrl ? (
        <>
          {layers.previous && <CoverImage url={layers.previous} dimmed={dimmed} />}
          <CoverImage key={layers.current} url={layers.current!} fadeIn={layers.previous !== undefined} dimmed={dimmed} />
          {/* Soft dark wash so the on-image (light) type treatment keeps its
              contrast over any photograph (#1294). One layer above both
              images, so it never doubles up during a crossfade. */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(rgba(6,14,40,0.45), rgba(6,14,40,0.25))',
              opacity: dimmed ? 0 : 1,
              transition: `opacity ${DIM_MS}ms ease-out`,
            }}
          />
        </>
      ) : (
        <>
          <Blob color="#3B82F6" opacity={0.12} size="clamp(360px, 58vw, 820px)" top="-16%" left="-10%" animation="agentStartDriftA" duration="60s" dimmed={dimmed} />
          <Blob color="#263757" opacity={0.10} size="clamp(320px, 50vw, 700px)" top="28%" left="56%" animation="agentStartDriftB" duration="72s" dimmed={dimmed} />
          <Blob color="#64748B" opacity={0.08} size="clamp(280px, 44vw, 620px)" top="58%" left="4%" animation="agentStartDriftC" duration="66s" dimmed={dimmed} />
        </>
      )}
    </Box>
  )
}

function CoverImage({ url, fadeIn, dimmed }: { url: string; fadeIn?: boolean; dimmed?: boolean }) {
  return (
    <Box
      data-testid="agent-start-backdrop-image"
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `url(${url})`,
        backgroundSize: 'cover',
        // Sky's covers carry the glowing logo in the middle of the frame, and
        // the column now sits in the top band above it. Nudging the focal point
        // down uses the vertical slack cover-scaling already leaves, so the logo
        // lands clear of the controls without the image being rescaled (#1294).
        backgroundPosition: 'center 65%',
        opacity: dimmed ? 0 : 1,
        transition: `opacity ${DIM_MS}ms ease-out`,
        animation: fadeIn ? 'agentStartCoverFadeIn 500ms ease-out' : 'none',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          transition: 'none',
        },
      }}
    />
  )
}

function Blob({ color, opacity, size, top, left, animation, duration, dimmed }: {
  color: string
  opacity: number
  size: string
  top: string
  left: string
  animation: string
  duration: string
  dimmed?: boolean
}) {
  return (
    <Box
      sx={{
        position: 'absolute',
        top,
        left,
        width: size,
        height: size,
        borderRadius: '50%',
        filter: 'blur(60px)',
        background: `radial-gradient(circle at 50% 50%, ${color} 0%, ${color} 35%, rgba(255,255,255,0) 70%)`,
        opacity: dimmed ? 0 : opacity,
        willChange: 'transform',
        transition: `opacity ${DIM_MS}ms ease-out`,
        animation: `${animation} ${duration} ease-in-out infinite`,
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          transition: 'none',
        },
      }}
    />
  )
}
